import { useContext, useEffect, useState } from 'react';
import { mixpanelTrack } from '@providers/Mixpanel';
import Shimmer from '@components/Shimmer';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { FormProviders, FormStatus } from '@typings/api-models';
import promiseRetry from 'promise-retry';
import Header from '@components/DesignLibrary/Header';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import PageContext from '@components/DesignLibrary/PageContext';
import Spacer from '@components/Spacer';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import removeNullish from '@helpers/removeNullish';
import classNames from 'classnames';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import DOMPurify from 'isomorphic-dompurify';
import {useFeatureFlag} from '@providers/FeatureFlags';
import NoodleAI from '@/components/Icons/NoodleAI';

import * as ApiModels from '@typings/api-models';
import trackEvents from '@/helpers/trackEvents';
import useSocketContext from '@providers/Socket/useSocketContext';
import { SocketMessageType } from '@/providers/Socket/SocketContext';
import Button from '@/components/DesignLibrary/Button';
import { useOpenWorkflowDocumentsPane } from '@panes/hooks';
import Files from '@components/Icons/Files';
import ProgressIndicator from '@components/ProgressIndicator';
import { useToast } from '@hooks';
import AnvilFormDetails from './AnvilFormDetails';
import OtherFormDetails from './OtherFormDetails';
import NoodleQuestionnaireFormDetails from './NoodleQuestionnaireFormDetails';
import s from './FormRequestDetails.module.scss';

type Props = {
    formRequestId: string;
    creatorSlug: string;
    onClose?: () => void;
};

type ClientFormRequest = Awaited<ReturnType<typeof tsClient.formRequests.getFormRequestById>>;

const FormRequestDetails: React.FC<Props> = ({ formRequestId, creatorSlug, onClose }) => {
  trackEvents.track('Render FormRequestDetails');
  const openWorkflowDocumentsPane = useOpenWorkflowDocumentsPane();
  const enableFormRequestAiSummary = useFeatureFlag('form-request-ai-summary');
  const allowUpdateToCurrentVersion = useFeatureFlag('update-questionnaire-version');
  const { addListener, removeListener, isInitialized: isSocketInitialized, joinChannels } = useSocketContext();
  const [formRequestDetails, setFormRequestDetails] = useState<ClientFormRequest| null>(null);
  const [summaryResponse, setSummaryResponse] = useState(formRequestDetails?.aiSummary ?? "");
  const [shouldForceComplete, setShouldForceComplete] = useState(false);

  const addToast = useToast();

  const { data: userWorkflow, getData: getUserWorkflowById } = useNoodleApi(tsClient.workflows.getUserWorkflowById, {
    toastOnError: true,
  });

  const { getData: getFormRequestById } = useNoodleApi(tsClient.formRequests.getFormRequestById, {
    toastOnError: true,
  });

  const { fetchingState: updateFormRequestFetchingState, getData: updateFormRequest } = useNoodleApi(tsClient.formRequests.updateFormRequest, {
    toastOnError: true,
  });

  const {
    fetchingState: updatingQuestionnaireToCurrentVersion,
    getData: updateNoodleFormRequestToCurrentVersionFn,
  } = useNoodleApi(tsClient.formRequests.updateNoodleFormRequestToCurrentVersion);

  const [enableAiSummary, setEnableAiSummary] = useState(false);

  const { isInDashboard, documentCount } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);

  useEffect(() => {
    setSummaryResponse(formRequestDetails?.aiSummary ?? '');
  }, [formRequestDetails?.aiSummary]);

  useEffect(() => {
    setEnableAiSummary(formRequestDetails?.template?.enableAiSummary ?? false);
  }, [formRequestDetails?.template.enableAiSummary]);

  useEffect(() => {
    mixpanelTrack('FormRequest - viewed', { creatorSlug, formRequestId });
  }, [creatorSlug, formRequestId]);

  useEffect(() => {
    if (formRequestId) {
      const fetchFormRequest = async (): Promise<void> => {
        const response = await getFormRequestById({ formRequestId });
        if (response.data) {
          setFormRequestDetails(response.data);
        }
      };
      fetchFormRequest();
    }
  }, [formRequestId, getFormRequestById]);

  useEffect(() => {
    if(formRequestDetails?.userWorkflowId) {
      getUserWorkflowById({
        userWorkflowId: formRequestDetails?.userWorkflowId,
      });
    }
  },[getUserWorkflowById, formRequestDetails?.userWorkflowId]);

  useEffect(() => {
    if (isSocketInitialized && formRequestDetails?.id) {
      joinChannels([('ai-summary/').concat(formRequestDetails?.id)]);
    }
  }, [isSocketInitialized, joinChannels, formRequestDetails?.id]);

  type socketResponse = {
    aiSummary: string;
  };

  const [formVersion, setFormVersion] = useState<number | null>(null);

  useEffect(() => {
    if (isSocketInitialized) {
      const listenerId = addListener({
        fn: async (data:unknown) => {
          const res = data as socketResponse;
          setSummaryResponse(res?.aiSummary);
        },
        messageType: SocketMessageType.FORM_SUMMARY_UPDATED,
      });
      return () => {
        removeListener(listenerId);
      };
    }
    return () => {};
  }, [addListener, removeListener, isSocketInitialized]);

  const handleEditFormClick = async (): Promise<void> => {
    const response = await updateFormRequest({
      formRequestId,
      status: FormStatus.IN_PROGRESS,
    });
    if (response.data) {
      setFormRequestDetails(response.data);
    }
  };

  const handleUpdateToLatestVersion = async (): Promise<void> => {
    if (formRequestDetails && formRequestDetails.template.provider === 'noodle' && formRequestDetails.userWorkflowId) {
      const response = await updateNoodleFormRequestToCurrentVersionFn({
        formRequestId,
      });
      if (response.data) {
        setFormRequestDetails(response.data);
      }
      if (response.error) {
        addToast(useToast.ToastTypeVariants.ERROR, `Failed to update to latest version: ${response.error.errors?.[0] || response.error.message}`);
      }
    }
  };

  const handleForgeComplete = async (): Promise<void> => {

    onClose?.();

    const formRequestResponse = await promiseRetry(async (retry) => {
      try {
        const formRequest = await getFormRequestById({ formRequestId });
        if (!formRequest.data?.response) {
          throw new Error('No formRequest.response so retry');
        }
        return formRequest;
      } catch(error) {
        return retry(error);
      }
    }, {
      factor: 1,
      minTimeout: 750,
      retries: 10,
    });

    if (formRequestResponse.data) {
      setFormRequestDetails({
        ...formRequestResponse.data,
        completedAt: formRequestResponse.data.completedAt || new Date().toISOString(),
        status: FormStatus.COMPLETED,
      });
    }
  };

  const handleReloadFormRequest = async (): Promise<void> => {
    setFormRequestDetails(null);
    if (formRequestId) {
      const response = await getFormRequestById({ formRequestId });
      if (response.data) {
        setFormRequestDetails(response.data);
      }
    }
  };

  if (!formRequestDetails) {
    return (
      <div className={s.wrapper}>
        <Shimmer />
      </div>
    );
  }

  const isSkipped = formRequestDetails.status === FormStatus.SKIPPED;
  const isCompleted = formRequestDetails.status === FormStatus.COMPLETED;
  const completed = formRequestDetails?.completedAt || isCompleted;

  const actions = [
    formRequestDetails.template.provider === 'noodle' && !completed
      ? {
        isPrimary: true,
        label: 'Submit Questionnaire',
        onClick: () => setShouldForceComplete(true),
      }
      : null,
    completed ? {label: 'Edit Responses', onClick: handleEditFormClick} : null,
    !isInDashboard && !isInUserProfile
      ? {
        href: getUrl(IDENTIFIERS.USER_PROFILE, {
          creatorSlug: creatorSlug || '',
          tab: formRequestDetails?.userWorkflowId ? 'activity' : 'chat',
          userWorkflowId: formRequestDetails?.userWorkflowId || undefined,
        }),
        label: 'Back to profile',
      }
      : null,
    isInDashboard && formRequestDetails.template.provider === 'noodle' && allowUpdateToCurrentVersion && !completed
      ? {
        isFetching: updatingQuestionnaireToCurrentVersion.isFetching,
        label: 'Update to current questionnaire version',
        onClick: handleUpdateToLatestVersion,
      }
      : null,
  ].filter(removeNullish);

  let bannerStatus = null;
  if (completed) {
    bannerStatus = { label: 'This form request has been completed.', status: 'success' as const };
  } else if (isSkipped) {
    bannerStatus = { label: 'This form request has been skipped.', status: 'warning' as const };
  }

  const showAiSummary = enableAiSummary && enableFormRequestAiSummary && completed && Boolean(summaryResponse)
  && (formRequestDetails?.template?.provider === FormProviders.Anvil || formRequestDetails?.template?.provider === FormProviders.Noodle);

  const formProvider = formRequestDetails.template.provider;

  const isBankruptcySchedulesTemplate = (
    formRequestDetails.template.provider === 'noodle'
    && formRequestDetails.template.providerLocator === 'noodle-bankruptcy-schedules-questionnaire-refactor'
  );
  return (
    <>
      <Header
        title={formRequestDetails.template.name}
        description={formVersion !== null ? <>Version: {formVersion}{isBankruptcySchedulesTemplate && <span className={s.beta}>Beta</span>}</> : ''}
        actions={actions}
      />
      <div className={s.formRequestDetailsContextWrapper}>
        <PageContext items={userWorkflowToContext(userWorkflow)} isFetching={!userWorkflow} />
        {!!documentCount && <div className={s.buttonWrapper}>
          <Button
            className={s.buttonCaseDoc}
            variant="secondary"
            size="sm"
            onClick={() => {
              if(formRequestDetails.userWorkflowId) {
                openWorkflowDocumentsPane({ workflowDocumentsId: formRequestDetails.userWorkflowId });
              }
            }}
          >
            <Files size={16} />
            <span>View case documents</span>
          </Button>
        </div>}
      </div>
      {!formRequestDetails?.completedAt && formProvider !== FormProviders.Noodle && <Spacer size={16}/>}
      {updatingQuestionnaireToCurrentVersion.isFetching && (
        <>
          <Spacer size={16} />
          <ProgressIndicator isCentered isPage />
        </>
      )}
      {!updatingQuestionnaireToCurrentVersion.isFetching && (
        <div className={s.container} {...!showAiSummary && {
          style: {
            display: 'contents',
          },
        }}>
          <div className={formProvider === FormProviders.Noodle
            ? classNames(
              s.noodleWrapper,
              formRequestDetails.completedAt && s.noodleWrapperCompleted,
            )
            : s.wrapper}>
            {bannerStatus
              ? <BannerStatus label={bannerStatus.label} status={bannerStatus.status}></BannerStatus>
              : undefined
            }
            {formProvider !== FormProviders.Noodle && <Spacer size={8} />}
            {!updateFormRequestFetchingState.isFetching && formProvider === FormProviders.Anvil && !isSkipped && (
              <AnvilFormDetails
                formRequest={formRequestDetails}
                onForgeComplete={handleForgeComplete}
              />
            )}
            {!updateFormRequestFetchingState.isFetching && formProvider === FormProviders.Noodle && !isSkipped && (
              <NoodleQuestionnaireFormDetails
                setFormVersion={setFormVersion}
                formRequest={formRequestDetails}
                onComplete={handleForgeComplete}
                onReload={handleReloadFormRequest}
                shouldForceComplete={shouldForceComplete} />
            )}
            {formProvider !== FormProviders.Anvil && formProvider !== FormProviders.Noodle && !isSkipped && (
              <OtherFormDetails
                formRequest={formRequestDetails}
              />
            )}
          </div>
          {showAiSummary && (
            <section className={s.aiSummary}>
              <div className={s.aiSummaryHeader}>
                <NoodleAI size={48} />
                <h1 className={s.aiSummaryTitle}><b>Glade Ai</b><br />Summary
                  {!summaryResponse && formRequestDetails.status === ApiModels.FormStatus.COMPLETED && ' is generating...'}
                </h1>
              </div>
              <div className={s.aiSummaryText} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summaryResponse) }} />
            </section>
          )}
        </div>
      )}
    </>
  );

};
export default FormRequestDetails;
